// src/components/BidModal.jsx

import React, { useState, useEffect, useRef, useCallback } from "react";
import useEscapeKey from "../hooks/useEscapeKey";
import "../App.css";

/* ── Accessible field ──────────────────────────────────────────────────────
   Every input previously used `placeholder` as its only label. Placeholders
   disappear on focus, aren't reliably announced by screen readers, and fail
   WCAG 3.3.2. This wires up a real <label>, aria-invalid, and
   aria-describedby so errors are announced when they appear.              */
function Field({
  id,
  label,
  hint,
  error,
  inputRef,
  className = "",
  ...inputProps
}) {
  const errorId = `${id}-error`;
  const hintId = `${id}-hint`;
  const describedBy = [error ? errorId : null, hint ? hintId : null]
    .filter(Boolean)
    .join(" ");

  return (
    <div className="field">
      <label htmlFor={id} className="field-label">
        {label}
      </label>
      <input
        id={id}
        ref={inputRef}
        className={`form-input ${className}`.trim()}
        aria-invalid={error ? "true" : undefined}
        aria-describedby={describedBy || undefined}
        {...inputProps}
      />
      {hint && (
        <p id={hintId} className="field-hint">
          {hint}
        </p>
      )}
      {error && (
        <p id={errorId} className="error-text" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

function BidModal({
  item,
  bid,
  setBid,
  firstName,
  setFirstName,
  lastName,
  setLastName,
  email,
  setEmail,
  phone,
  setPhone,
  onSubmit,
  onClose,
  message,
  messageTone = "success",
  submitting = false,
}) {
  const [errors, setErrors] = useState({});
  const firstInputRef = useRef(null);
  const dialogRef = useRef(null);

  const clearError = useCallback((key) => {
    setErrors((prev) => (prev[key] ? { ...prev, [key]: undefined } : prev));
  }, []);

  /* Escape to close plus background scroll lock, shared with Lightbox. Both
     components had their own copy of this effect, and they had already drifted
     — this one didn't restore the previous overflow value correctly. */
  useEscapeKey(onClose, true, true);

  useEffect(() => {
    firstInputRef.current?.focus();
  }, []);

  /* Focus trap — without this, Tab walks out of the dialog and into the page
     behind it, which is disorienting on a screen reader and on a keyboard. */
  const handleKeyDown = (e) => {
    if (e.key !== "Tab" || !dialogRef.current) return;
    const focusable = dialogRef.current.querySelectorAll(
      'button, input, select, textarea, a[href], [tabindex]:not([tabindex="-1"])'
    );
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  if (!item) return null;

  const formatZAR = (amount) =>
    new Intl.NumberFormat("en-ZA", {
      style: "currency",
      currency: "ZAR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(amount || 0);

  const minBid = item.reservation_price;
  const step = item.min_increment;

  /* The backend rejects any bid where
       (amount - reservation_price) % min_increment != 0
     Snapping here means the bidder never sees that error. */
  const snapToIncrement = (value) => {
    if (!Number.isFinite(value)) return minBid;
    const steps = Math.round((value - minBid) / step);
    return Math.max(minBid, minBid + steps * step);
  };

  const validateForm = () => {
    const newErrors = {};

    if (!firstName.trim()) newErrors.firstName = "Please enter your first name.";
    if (!lastName.trim()) newErrors.lastName = "Please enter your last name.";

    /* The old pattern capped the TLD at 4 characters, rejecting .photography,
       .gallery and similar. Real validation belongs on the server; this only
       needs to catch obvious typos. */
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim()))
      newErrors.email = "Please enter a valid email address.";

    const digits = phone.replace(/[\s()-]/g, "");
    if (!/^\+?\d{9,15}$/.test(digits))
      newErrors.phone = "Please enter a valid phone number, e.g. +27 82 123 4567.";

    if (!Number.isFinite(bid) || bid < minBid)
      newErrors.bid = `The minimum bid is ${formatZAR(minBid)}.`;
    else if (Math.abs((bid - minBid) % step) > 0.001)
      newErrors.bid = `Bids go up in steps of ${formatZAR(step)}.`;

    setErrors(newErrors);

    // Move focus to the first problem so it isn't missed off-screen on a phone.
    const firstKey = Object.keys(newErrors)[0];
    if (firstKey) {
      document.getElementById(`bid-${firstKey}`)?.focus();
    }

    return Object.keys(newErrors).length === 0;
  };

  return (
    <div
      className="modal-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="bid-modal-title"
      onKeyDown={handleKeyDown}
      // Click the backdrop to dismiss, but not clicks inside the panel.
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-content" ref={dialogRef}>
        <button
          className="close-button"
          onClick={onClose}
          aria-label="Close"
          type="button"
        >
          ✖
        </button>

        <h2 id="bid-modal-title">{item.title}</h2>
        <p className="modal-minimum">
          Minimum bid <strong>{formatZAR(minBid)}</strong>
        </p>

        <Field
          id="bid-firstName"
          label="First name"
          type="text"
          autoComplete="given-name"
          inputRef={firstInputRef}
          value={firstName}
          error={errors.firstName}
          onChange={(e) => {
            setFirstName(e.target.value);
            clearError("firstName");
          }}
        />

        <Field
          id="bid-lastName"
          label="Last name"
          type="text"
          autoComplete="family-name"
          value={lastName}
          error={errors.lastName}
          onChange={(e) => {
            setLastName(e.target.value);
            clearError("lastName");
          }}
        />

        <Field
          id="bid-email"
          label="Email address"
          type="email"
          inputMode="email"
          autoComplete="email"
          hint="Your bid confirmation is sent here."
          value={email}
          error={errors.email}
          onChange={(e) => {
            setEmail(e.target.value);
            clearError("email");
          }}
        />

        <Field
          id="bid-phone"
          label="Phone number"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          placeholder="+27 82 123 4567"
          value={phone}
          error={errors.phone}
          onChange={(e) => {
            setPhone(e.target.value);
            clearError("phone");
          }}
        />

        {/* ── Bid amount ────────────────────────────────────────────────────
            Replaces the range slider, whose max was
              reservation_price + 11100 * min_increment
            — roughly R3,700 per pixel at R250 increments, impossible to set an
            intended amount with a thumb. inputMode="decimal" raises the
            numeric keypad; the +/- buttons handle the common case. */}
        <div className="field bid-amount-field">
          <label htmlFor="bid-bid" className="field-label">
            Your bid
          </label>

          <div className="bid-stepper">
            <button
              type="button"
              className="stepper-button"
              onClick={() => {
                setBid(Math.max(minBid, bid - step));
                clearError("bid");
              }}
              disabled={bid <= minBid}
              aria-label={`Decrease bid by ${formatZAR(step)}`}
            >
              −
            </button>

            <input
              id="bid-bid"
              className="form-input bid-amount"
              type="number"
              inputMode="decimal"
              min={minBid}
              step={step}
              value={bid}
              aria-invalid={errors.bid ? "true" : undefined}
              aria-describedby={errors.bid ? "bid-bid-error" : "bid-bid-hint"}
              onChange={(e) => {
                setBid(parseFloat(e.target.value));
                clearError("bid");
              }}
              onBlur={(e) => setBid(snapToIncrement(parseFloat(e.target.value)))}
            />

            <button
              type="button"
              className="stepper-button"
              onClick={() => {
                setBid((Number.isFinite(bid) ? bid : minBid) + step);
                clearError("bid");
              }}
              aria-label={`Increase bid by ${formatZAR(step)}`}
            >
              +
            </button>
          </div>

          <p id="bid-bid-hint" className="field-hint">
            In steps of {formatZAR(step)}
          </p>

          {errors.bid && (
            <p id="bid-bid-error" className="error-text" role="alert">
              {errors.bid}
            </p>
          )}
        </div>

        {/* `submitting` guards against a double-tap placing two bids — easy to
            do on a phone when the network is slow and nothing visibly changes
            after the first tap. */}
        <button
          className="submit-button"
          type="button"
          disabled={submitting}
          onClick={() => {
            if (!submitting && validateForm()) onSubmit();
          }}
        >
          {submitting ? "Placing bid…" : `Place bid of ${formatZAR(bid)}`}
        </button>

        {/* messageTone is "warning" when the bid saved but the confirmation
            email failed. Amber rather than green: the outcome is good but
            incomplete, and the bidder needs to notice rather than skim past
            what reads as routine success. */}
        {message && (
          <p
            className={`confirmation-message${
              messageTone === "warning" ? " confirmation-warning" : ""
            }`}
            role="status"
            aria-live="polite"
          >
            {message}
          </p>
        )}
      </div>
    </div>
  );
}

export default BidModal;